/** Pure helpers over arrays of die values (1..6). */

export function counts(dice: number[]): number[] {
  const c = [0, 0, 0, 0, 0, 0, 0];
  for (const d of dice) c[d] = (c[d] ?? 0) + 1;
  return c;
}

export function sum(dice: number[]): number {
  return dice.reduce((a, b) => a + b, 0);
}

export function sumOf(dice: number[], value: number): number {
  return dice.filter(d => d === value).length * value;
}

/** Highest value appearing at least `n` times, or 0. */
export function highestOfAKind(dice: number[], n: number): number {
  const c = counts(dice);
  for (let v = 6; v >= 1; v--) if ((c[v] ?? 0) >= n) return v;
  return 0;
}

export function hasOfAKind(dice: number[], n: number): boolean {
  return highestOfAKind(dice, n) > 0;
}

export function maxCount(dice: number[]): number {
  return Math.max(...counts(dice));
}

/** Values with count >= 2, descending. Four-of-a-kind counts as one pair. */
export function pairValues(dice: number[]): number[] {
  const c = counts(dice);
  const out: number[] = [];
  for (let v = 6; v >= 1; v--) if ((c[v] ?? 0) >= 2) out.push(v);
  return out;
}

/** Longest run of consecutive distinct values. */
export function longestRun(dice: number[]): number {
  const c = counts(dice);
  let best = 0;
  let run = 0;
  for (let v = 1; v <= 6; v++) {
    if ((c[v] ?? 0) > 0) {
      run++;
      best = Math.max(best, run);
    } else run = 0;
  }
  return best;
}

/** True if every value in `values` appears at least once. */
export function containsAll(dice: number[], values: number[]): boolean {
  const c = counts(dice);
  return values.every(v => (c[v] ?? 0) > 0);
}

/** Exact 3 + 2 of two different values. */
export function isFullHouse(dice: number[]): boolean {
  const c = counts(dice)
    .slice(1)
    .filter(n => n > 0)
    .sort();
  return c.length === 2 && c[0] === 2 && c[1] === 3;
}

/** Full house that also accepts five of a kind (Hasbro joker rules do not; this is for variants that do). */
export function isFullHouseLoose(dice: number[]): boolean {
  return isFullHouse(dice) || maxCount(dice) === dice.length;
}

export function isAllSame(dice: number[]): boolean {
  return dice.length > 0 && dice.every(d => d === dice[0]);
}

/** Sorted copy, ascending. */
export function sorted(dice: number[]): number[] {
  return [...dice].sort((a, b) => a - b);
}

/** Canonical key for a multiset of dice (order-independent). */
export function multisetKey(dice: number[]): string {
  return sorted(dice).join('');
}
